import { useCallback, useMemo, useState } from 'react';

import pick from 'lodash/pick';
import { useIntl } from 'react-intl-next';

import { useDefaultCatch } from '@atlassian/eoc-catch';
import { assert, AssertionError, errorToastProps, successToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { messages as commonMessages } from '../../common/messages';
import { type ActionCallback, LayerTypes, PageModalType } from '../../common/types';
import { endpoints as listEndpoints } from '../../services/endpoints/list';
import { type UpdateScheduleParameters } from '../../services/endpoints/list/types';
import { getSchedulesResource, getUiPreferencesResource } from '../../services/resources';
import { isNullOrUndefined } from '../../utils/validators';
import { useActions as usePageActions } from '../page-store';
import { useSectionUiPreferences } from '../preferences';
import { UI_PREFERENCES_DEFAULT_VALUES } from '../preferences/constants';
import { useSchedule } from '../schedule-store/helpers';

import { messages } from './messages';

// TODO: make similar to section ui preference hook
const useUpdateScheduleService = (scheduleId: string) => {
	const { update: updateSchedulesResource } = useResource(getSchedulesResource);
	const flagApi = useFlags();
	const intlApi = useIntl();
	const defaultCatch = useDefaultCatch();

	const [isLoading, setIsLoading] = useState(false);

	const updateSchedule = useCallback(
		async (
			{ enabled, timezone, description, name }: Omit<UpdateScheduleParameters, 'scheduleId'>,
			actionCallback?: ActionCallback,
		) => {
			setIsLoading(true);

			const updatedFields = {
				...(!isNullOrUndefined(enabled) && { enabled }),
				...(!isNullOrUndefined(name) && { name }),
				...(!isNullOrUndefined(description) && { description }),
				...(!isNullOrUndefined(timezone) && { timezone }),
			};

			try {
				const response = await listEndpoints.updateSchedule({
					scheduleId,
					...updatedFields,
				});

				updateSchedulesResource((prev) => {
					const schedules = [...(prev?.schedules ?? [])];
					const selectedIdx = schedules.findIndex(({ id }) => id === scheduleId);

					assert(
						selectedIdx !== -1,
						new AssertionError('eoc-schedule', 'use-update-schedule-service: schedule not found'),
					);

					schedules[selectedIdx] = pick(response, [
						'id',
						'name',
						'description',
						'timezone',
						'enabled',
						'utcTimeOffsetMillis',
					]);

					return {
						schedules,
					};
				});

				actionCallback?.onSuccess?.();
			} catch (error) {
				defaultCatch<void>(
					{
						id: 'useUpdateScheduleService-updateSchedule',
						httpErrorHandler: (err) => {
							if (err.message) {
								flagApi.showFlag({
									...errorToastProps(),
									description: err.message,
									title: intlApi.formatMessage(commonMessages.error),
								});
							} else {
								flagApi.showFlag({
									...errorToastProps(),
									title: intlApi.formatMessage(messages.updateScheduleErrorTitle),
									actions: [
										{
											content: intlApi.formatMessage(commonMessages.tryAgain),
											onClick: () =>
												updateSchedule({
													enabled,
													timezone,
													description,
													name,
												}),
										},
									],
								});
							}
						},
					},
					error,
				);

				actionCallback?.onError?.();
			} finally {
				setIsLoading(false);
			}
		},
		[defaultCatch, flagApi, intlApi, scheduleId, updateSchedulesResource],
	);

	return { updateSchedule, isLoading };
};

const usePartialUpdateScheduleService = (
	scheduleId: string,
	field: 'description' | 'name' | 'enabled' | 'timezone',
) => {
	const { isLoading, updateSchedule } = useUpdateScheduleService(scheduleId);

	const partialUpdate = useCallback(
		(value: any, actionCallback?: ActionCallback) =>
			updateSchedule({ [field]: value }, actionCallback),
		[field, updateSchedule],
	);

	return { isLoading, partialUpdate };
};

const useDeleteScheduleService = (scheduleId: string) => {
	const { update: updateSchedulesResource } = useResource(getSchedulesResource);
	const { closePageModal } = usePageActions();
	const flagApi = useFlags();
	const intlApi = useIntl();
	const defaultCatch = useDefaultCatch();

	const [isLoading, setIsLoading] = useState(false);

	const deleteSchedule = useCallback(async () => {
		setIsLoading(true);

		try {
			await listEndpoints.deleteSchedule({ scheduleId });

			closePageModal(PageModalType.DeleteSchedule);
			closePageModal(PageModalType.EditMode);

			updateSchedulesResource((prev) => {
				return {
					schedules: (prev?.schedules ?? []).filter(({ id }) => id !== scheduleId),
				};
			});

			flagApi.showFlag({
				...successToastProps(),
				title: intlApi.formatMessage(messages.deleteScheduleSuccessTitle),
				isAutoDismiss: true,
			});
		} catch (error) {
			defaultCatch<void>(
				{
					id: 'useDeleteScheduleService-deleteSchedule',
					httpErrorHandler: (err) => {
						if (err.message) {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: intlApi.formatMessage(commonMessages.error),
							});
						} else {
							flagApi.showFlag({
								...errorToastProps(),
								title: intlApi.formatMessage(messages.deleteScheduleErrorTitle),
								isAutoDismiss: true,
							});
						}
					},
				},
				error,
			);
		} finally {
			setIsLoading(false);
		}
	}, [defaultCatch, flagApi, intlApi, closePageModal, scheduleId, updateSchedulesResource]);

	return { deleteSchedule, isLoading };
};

const useLayers = ({ withForwarding }: { withForwarding: boolean }) => {
	const intlApi = useIntl();
	const [{ id: scheduleId }] = useSchedule();
	const { data: uiPreferences } = useResource(getUiPreferencesResource);
	const { scheduleOrder } = useSectionUiPreferences();

	const showAllLayers = useMemo(
		() =>
			uiPreferences?.schedulePreferences.find(({ id }) => id === scheduleId)?.showAllLayers ??
			UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.showAllLayers,
		[scheduleId, uiPreferences?.schedulePreferences],
	);

	const layers = useMemo(
		() => ({
			base: {
				label: intlApi.formatMessage(messages.base),
				value: LayerTypes.BaseLayer,
			},
			override: {
				label: intlApi.formatMessage(messages.override),
				value: LayerTypes.OverrideLayer,
			},
			forwarding: {
				label: intlApi.formatMessage(messages.forwarding),
				value: LayerTypes.ForwardingLayer,
			},
			final: {
				label: intlApi.formatMessage(messages.final),
				value: LayerTypes.FinalLayer,
			},
		}),
		[intlApi],
	);

	// TODO: ensure the order is correct
	const ordered = useMemo(() => {
		if (scheduleOrder === 'finalSchedule') {
			return [
				layers.final,
				...(withForwarding ? [layers.forwarding] : []),
				layers.override,
				layers.base,
			];
		} else {
			return [
				layers.base,
				layers.override,
				...(withForwarding ? [layers.forwarding] : []),
				layers.final,
			];
		}
	}, [layers, scheduleOrder, withForwarding]);

	return {
		/** @description Layers adjusted for all ui preferences */
		preferedLayers: showAllLayers ? ordered : [layers.final],
		/** @description Layers adjusted for only order ui preference */
		allLayers: ordered,
		/** @description This can be used to access labels for the layers */
		layers,
	};
};

export {
	useUpdateScheduleService,
	usePartialUpdateScheduleService,
	useDeleteScheduleService,
	useLayers,
};
